import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { SectionLabel } from '@perch/ui';
import { useAuth } from './hooks/useAuth';
import { useBookings } from './hooks/useBookings';
import { useProfile } from './hooks/useProfile';
import { useToast } from './hooks/useToast';
import { useDevMode, isDevMode } from './hooks/useDevMode';
import { useOnboardingState } from './hooks/useOnboardingState';
import { useApprovalStatus } from './hooks/useApprovalStatus';
import { useUrlFilters } from './hooks/useUrlFilters';
import { Confetti } from './components/ui/Confetti';
import { ToastContainer } from './components/ui/Toast';
import { SettingsPanel } from './components/settings';
import type { SettingsTab } from './components/settings';
import { DashboardSkeleton } from './components/ui/Skeleton';
import { SummaryCard, TripCard, OnboardingGuide, EmptyDashboard, FilterBar, PendingBookingCard, AutoForwardCTA } from './components/dashboard';
import { AddBookingModal } from './components/add-booking';
import { usePendingLookups } from './hooks/usePendingLookups';
import { LandingPage } from './components/landing';
import { SiteHeader } from './components/layout';
import { AuthModal } from './components/auth';
import { WelcomeFlow } from './components/welcome';
import { WaitlistPending } from './components/waitlist';
import { DevModeBanner, ScenarioGallery } from './components/dev';
import { PrivacyPage } from './pages/PrivacyPage';
import { TermsPage } from './pages/TermsPage';
import { SharePage } from './pages/SharePage';
import type { Booking, BookingStats, PendingLookup, UserProfile } from './lib/types';
import type { User, Session } from '@supabase/supabase-js';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const FILTER_BAR_MIN_BOOKINGS = 5;

// Redirect old /settings/:tab? URLs to /?settings=<tab>.
// Preserves existing query params so flows like Stripe Checkout return
// (/settings/billing?checkout_session_id=...) don't lose their params.
// Exported for tests.
export function SettingsRedirect() {
  const { tab } = useParams<{ tab?: string }>();
  const location = useLocation();
  const settingsTab = tab || 'profile';
  const params = new URLSearchParams(location.search);
  params.set('settings', settingsTab);
  return <Navigate to={`/?${params.toString()}`} replace />;
}

// Shared dashboard props interface - matches what components expect
interface DashboardProps {
  user: User;
  session: Session | null;
  watchingBookings: Booking[];
  historyBookings: Booking[];
  filteredBookings: Booking[];
  stats: BookingStats;
  bookingsLoading: boolean;
  portfolioValue: number;
  lifetimeSavings: number;
  priceHistoryMap: Record<string, number[]>;
  archivedCount: number;
  clearArchivedCount: () => void;
  loadBookings: () => void;
  applyFilters: (filter: import('./lib/types').FilterType, search: string, sort: import('./lib/types').SortType) => void;
  stopWatching: (bookingId: string) => Promise<boolean>;
  deleteBooking: (bookingId: string) => Promise<boolean>;
  profile: UserProfile | null;
  signOut: () => void;
  bannerInfo?: { label: string; counts: string } | null;
  openAddBookingOnMount?: boolean;
  onAddBookingMountConsumed?: () => void;
  onOpenSettings?: (tab?: string) => void;
}

// Main dashboard component - receives all data as props
function Dashboard({
  user,
  session,
  watchingBookings,
  historyBookings,
  filteredBookings,
  stats,
  bookingsLoading,
  portfolioValue,
  lifetimeSavings,
  priceHistoryMap,
  archivedCount,
  clearArchivedCount,
  loadBookings,
  applyFilters,
  stopWatching,
  deleteBooking,
  profile,
  signOut,
  bannerInfo,
  openAddBookingOnMount,
  onAddBookingMountConsumed,
  onOpenSettings,
}: DashboardProps) {
  const toast = useToast();
  const navigate = useNavigate();
  const { filter, sort, search, setFilter, setSort, setSearch, clearFilters } = useUrlFilters();
  const { pendingLookups, dismissPending, confirmPending } = usePendingLookups(session);
  const showFilterBar = stats.total_bookings >= FILTER_BAR_MIN_BOOKINGS;
  const isFiltering = filter !== 'all' || search !== '' || sort !== 'date-desc';

  // Apply filters when filter state changes
  useEffect(() => {
    if (showFilterBar) {
      applyFilters(filter, search, sort);
    }
  }, [filter, search, sort, showFilterBar, applyFilters]);

  // Modal state
  const [showAddBooking, setShowAddBooking] = useState(false);
  // When non-null, the add-booking modal opens pre-filled with this pending
  // lookup's parsed data. After successful add-booking, this lookup is
  // promoted to status='confirmed'. See onBookingCreated wiring below.
  const [confirmingPending, setConfirmingPending] = useState<PendingLookup | null>(null);

  // Open add booking modal if requested from WelcomeFlow
  useEffect(() => {
    if (openAddBookingOnMount) {
      setShowAddBooking(true);
      onAddBookingMountConsumed?.();
    }
  }, [openAddBookingOnMount, onAddBookingMountConsumed]);

  // Inline expansion state (replaces modal for booking detail)
  const [expandedBookingId, setExpandedBookingId] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);

  // Collapse expanded card on Escape key
  useEffect(() => {
    if (!expandedBookingId) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setExpandedBookingId(null);
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [expandedBookingId]);

  // Show toast when bookings are auto-archived
  useEffect(() => {
    if (archivedCount > 0) {
      toast.success(
        `${archivedCount} flight${archivedCount > 1 ? 's' : ''} archived`,
        'Past flights have been moved to your archive.'
      );
      clearArchivedCount();
    }
  }, [archivedCount, toast, clearArchivedCount]);

  // Toggle booking detail inline expansion (accordion — one at a time)
  const toggleBookingDetail = (booking: Booking) => {
    setExpandedBookingId(prev => prev === booking.id ? null : booking.id);
  };

  // Handle successful booking add
  const handleBookingAdded = (isAward: boolean, amount: number) => {
    setShowConfetti(true);
    toast.success(
      'Booking added!',
      isAward
        ? `Now tracking ${amount.toLocaleString()} points booking`
        : `Now tracking $${amount} booking`
    );
    loadBookings();
  };

  return (
    <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
      {/* Confetti celebration */}
      <Confetti show={showConfetti} onComplete={() => setShowConfetti(false)} />

      {/* Toast notifications */}
      <ToastContainer toasts={toast.toasts} onDismiss={toast.dismissToast} />

      {/* Header */}
      <SiteHeader
        mode="dashboard"
        user={user}
        onAddBooking={() => setShowAddBooking(true)}
        onOpenSettings={onOpenSettings}
        onSignOut={signOut}
      />

      {/* Dev Mode Banner */}
      {bannerInfo && <DevModeBanner label={bannerInfo.label} counts={bannerInfo.counts} />}

      {/* Account deletion banner */}
      {profile?.deletion_requested_at && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-4">
          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/50 rounded-xl p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
            <div className="flex-1">
              <p className="text-sm text-red-800 dark:text-red-200">
                Your account is scheduled for deletion on{' '}
                <span className="font-medium">
                  {new Date(new Date(profile.deletion_requested_at).getTime() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </span>.{' '}
                <button
                  onClick={() => navigate('/settings/account')}
                  className="underline font-medium hover:opacity-80"
                >
                  Cancel in Settings
                </button>
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] space-y-6">

        {/* Full dashboard skeleton for initial load */}
        {bookingsLoading && stats.total_bookings === 0 && (
          <DashboardSkeleton />
        )}

        {/* Trust Dashboard - New Design */}
        {!bookingsLoading && (
          <>
            {/* Summary Card - Trust-focused stats */}
            {stats.total_bookings > 0 && (
              <SummaryCard stats={stats} portfolioValue={portfolioValue} lifetimeSavings={lifetimeSavings} />
            )}

            {/* Onboarding Guide - phases out based on booking count */}
            <OnboardingGuide
              totalBookings={stats.total_bookings}
              onAddBooking={() => setShowAddBooking(true)}
            />

            {/* Filter Bar - shown for 5+ bookings */}
            {showFilterBar && (
              <FilterBar
                filter={filter}
                sort={sort}
                search={search}
                onFilterChange={setFilter}
                onSortChange={setSort}
                onSearchChange={setSearch}
              />
            )}

            {/* Filtered view - when any filter/search/sort is active */}
            {showFilterBar && isFiltering ? (
              filteredBookings.length > 0 ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <SectionLabel>
                      Results · {filteredBookings.length}
                    </SectionLabel>
                  </div>
                  <div className="space-y-3">
                    {filteredBookings.map((booking, index) => (
                      <TripCard
                        key={booking.id}
                        booking={booking}
                        index={index}
                        onClick={toggleBookingDetail}
                        isExpanded={expandedBookingId === booking.id}
                        onStopWatching={stopWatching}
                        onDeleteBooking={deleteBooking}
                        onCollapse={() => setExpandedBookingId(null)}
                        priceHistory={priceHistoryMap[booking.id]}
                      />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-stone-500 dark:text-stone-400 mb-2">
                    No flights match your filters.
                  </p>
                  <button
                    onClick={clearFilters}
                    className="text-sm text-stone-900 dark:text-stone-100 underline hover:no-underline"
                  >
                    Clear Filters
                  </button>
                </div>
              )
            ) : (
              <>
                {/* Pending Lookups */}
                {pendingLookups.length > 0 && (
                  <div className="space-y-3">
                    {pendingLookups.map((pending) => (
                      <PendingBookingCard
                        key={pending.id}
                        pending={pending}
                        onConfirm={(p) => {
                          // Open the add-booking modal pre-filled with the
                          // parsed data. Once the user reviews and creates the
                          // booking, onBookingCreated (below) promotes this
                          // pending lookup to status='confirmed'.
                          if (p.lookup_result) {
                            setConfirmingPending(p);
                            setShowAddBooking(true);
                          }
                        }}
                        onAddManually={(p) => {
                          dismissPending(p.id);
                          setShowAddBooking(true);
                        }}
                        onDismiss={(id) => dismissPending(id)}
                      />
                    ))}
                  </div>
                )}

                {/* Watching Section */}
                {watchingBookings.length > 0 && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <SectionLabel>
                        Active · {watchingBookings.length}
                      </SectionLabel>
                      <button
                        onClick={() => loadBookings()}
                        className="p-2 -m-2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 transition-colors"
                        title="Refresh bookings"
                        aria-label="Refresh bookings"
                      >
                        <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                    <div className="space-y-3">
                      {watchingBookings.map((booking, index) => (
                        <TripCard
                          key={booking.id}
                          booking={booking}
                          index={index}
                          onClick={toggleBookingDetail}
                          isExpanded={expandedBookingId === booking.id}
                          onStopWatching={stopWatching}
                          onDeleteBooking={deleteBooking}
                          onCollapse={() => setExpandedBookingId(null)}
                          priceHistory={priceHistoryMap[booking.id]}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* History Section */}
                {historyBookings.length > 0 && (
                  <div className="space-y-4">
                    <SectionLabel>
                      History · {historyBookings.length}
                    </SectionLabel>
                    <div className="space-y-3 opacity-60">
                      {historyBookings.map((booking, index) => (
                        <TripCard
                          key={booking.id}
                          booking={booking}
                          index={index}
                          onClick={toggleBookingDetail}
                          isExpanded={expandedBookingId === booking.id}
                          onStopWatching={stopWatching}
                          onDeleteBooking={deleteBooking}
                          onCollapse={() => setExpandedBookingId(null)}
                          priceHistory={priceHistoryMap[booking.id]}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Auto-Forward CTA — shown after first booking */}
                <AutoForwardCTA bookingCount={stats.total_bookings} />
              </>
            )}

            {/* Empty State - No bookings at all */}
            {stats.total_bookings === 0 && (
              <EmptyDashboard onAddManually={() => setShowAddBooking(true)} />
            )}
          </>
        )}

        {/* Add Booking Modal */}
        <AddBookingModal
          isOpen={showAddBooking}
          onClose={() => {
            setShowAddBooking(false);
            setConfirmingPending(null);
          }}
          session={session}
          user={user}
          onSuccess={handleBookingAdded}
          onLookupSubmitted={() => loadBookings()}
          initialPreview={confirmingPending?.lookup_result ?? null}
          onBookingCreated={() => {
            // Promote the pending lookup to confirmed now that a real
            // tracked_booking exists. Before this fix, confirmPending was
            // called in isolation — the lookup vanished but no booking was
            // ever created.
            if (confirmingPending) {
              confirmPending(confirmingPending.id);
              setConfirmingPending(null);
            }
          }}
        />
      </div>
    </div>
  );
}

// Mock Supabase user for dev mode - complete User type with all required fields
const mockSupabaseUser: User = {
  id: 'dev-user',
  email: 'dev@example.com',
  app_metadata: {},
  user_metadata: {},
  aud: 'authenticated',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  role: '',
  confirmation_sent_at: undefined,
  confirmed_at: undefined,
  last_sign_in_at: undefined,
  phone: '',
  email_confirmed_at: undefined,
  phone_confirmed_at: undefined,
  is_anonymous: false,
};

// Dev mode app - uses mock data
function DevApp() {
  const devMode = useDevMode()!; // We know it exists because we checked isDevMode
  const allDevBookings = [...devMode.watchingBookings, ...devMode.historyBookings];

  return (
    <Dashboard
      user={mockSupabaseUser}
      session={null}
      watchingBookings={devMode.watchingBookings}
      historyBookings={devMode.historyBookings}
      filteredBookings={allDevBookings}
      stats={devMode.stats}
      bookingsLoading={false}
      portfolioValue={0}
      lifetimeSavings={0}
      priceHistoryMap={{}}
      archivedCount={0}
      clearArchivedCount={() => {}}
      loadBookings={() => {}}
      applyFilters={() => {}}
      stopWatching={async () => true}
      deleteBooking={async () => true}
      profile={devMode.profile}
      signOut={() => {}}
      bannerInfo={devMode.bannerInfo}
    />
  );
}

// Authenticated app - only mounts when user is logged in
function AuthenticatedApp({ user, session, signOut }: { user: User; session: Session | null; signOut: () => void }) {
  const {
    watchingBookings,
    historyBookings,
    filteredBookings,
    stats,
    loading: bookingsLoading,
    loadBookings,
    applyFilters,
    archivedCount,
    clearArchivedCount,
    portfolioValue,
    lifetimeSavings,
    priceHistoryMap,
    stopWatching,
    deleteBooking,
  } = useBookings();
  const { profile, loadProfile } = useProfile(user.id);
  const { isApproved, loading: approvalLoading } = useApprovalStatus(user);
  const { isOnboardingComplete, completeOnboarding } = useOnboardingState(profile, user.id);
  const [openAddBookingOnMount, setOpenAddBookingOnMount] = useState(false);

  // Settings overlay state — null = closed, string = open on that tab
  const [settingsTab, setSettingsTab] = useState<SettingsTab | null>(null);

  // Read ?settings= query param on mount for deep links
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('settings');
    if (tab && ['profile', 'notifications', 'billing', 'preferences', 'account'].includes(tab)) {
      setSettingsTab(tab as SettingsTab);
    }
  }, []);

  // Sync URL when settings state changes
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (settingsTab) {
      params.set('settings', settingsTab);
    } else {
      params.delete('settings');
    }
    const newUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
    window.history.replaceState(null, '', newUrl);
  }, [settingsTab]);

  const handleOpenSettings = (tab?: string) => {
    const validTab = tab && ['profile', 'notifications', 'billing', 'preferences', 'account'].includes(tab)
      ? tab as SettingsTab
      : 'profile';
    setSettingsTab(validTab);
  };

  const handleCloseSettings = () => {
    setSettingsTab(null);
  };

  // Waitlist approval gate — must be approved before accessing anything
  if (approvalLoading) {
    return (
      <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
        <SiteHeader mode="loading" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
          <DashboardSkeleton />
        </div>
      </div>
    );
  }

  if (!isApproved) {
    return <WaitlistPending onSignOut={signOut} />;
  }

  // Welcome flow for first-time users with no bookings
  if (!isOnboardingComplete && stats.total_bookings === 0 && !bookingsLoading && session) {
    return (
      <WelcomeFlow
        user={user}
        session={session}
        profile={profile}
        onComplete={() => {
          completeOnboarding();
          loadBookings();
        }}
        onAddManually={() => setOpenAddBookingOnMount(true)}
      />
    );
  }

  return (
    <>
      <Dashboard
        user={user}
        session={session}
        watchingBookings={watchingBookings}
        historyBookings={historyBookings}
        filteredBookings={filteredBookings}
        stats={stats}
        bookingsLoading={bookingsLoading}
        portfolioValue={portfolioValue}
        lifetimeSavings={lifetimeSavings}
        priceHistoryMap={priceHistoryMap}
        archivedCount={archivedCount}
        clearArchivedCount={clearArchivedCount}
        loadBookings={loadBookings}
        applyFilters={applyFilters}
        stopWatching={stopWatching}
        deleteBooking={deleteBooking}
        profile={profile}
        signOut={signOut}
        openAddBookingOnMount={openAddBookingOnMount}
        onAddBookingMountConsumed={() => setOpenAddBookingOnMount(false)}
        onOpenSettings={handleOpenSettings}
      />

      {/* Settings overlay panel */}
      <SettingsPanel
        isOpen={settingsTab !== null}
        activeTab={settingsTab || 'profile'}
        onTabChange={setSettingsTab}
        onClose={handleCloseSettings}
        user={user}
        session={session}
        profile={profile}
        stats={stats}
        lifetimeSavings={lifetimeSavings}
        signOut={signOut}
        loadProfile={loadProfile}
      />
    </>
  );
}

// Real app - handles auth gate, delegates to AuthenticatedApp
// Settings route lives inside the auth boundary so it shares resolved auth state
function RealApp() {
  const { user, session, loading: authLoading, signIn, signInWithGoogle, signInWithMagicLink, signOut } = useAuth();
  const [showSignIn, setShowSignIn] = useState(false);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-stone-50 dark:bg-stone-950">
        <SiteHeader mode="loading" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
          <DashboardSkeleton />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <LandingPage onGetStarted={() => setShowSignIn(true)} />
        <AuthModal
          isOpen={showSignIn}
          onClose={() => setShowSignIn(false)}
          onSignIn={signInWithGoogle}
          onSignInWithMagicLink={signInWithMagicLink}
          onDevSignIn={signIn}
        />
      </>
    );
  }

  return (
    <Routes>
      <Route
        path="/settings/:tab?"
        element={<SettingsRedirect />}
      />
      <Route
        path="*"
        element={<AuthenticatedApp user={user} session={session} signOut={signOut} />}
      />
    </Routes>
  );
}

// AppContent decides which version to render (dev vs real)
function AppContent() {
  return isDevMode ? <DevApp /> : <RealApp />;
}

// App root - routes
// Public routes (privacy, terms) are handled here.
// Authenticated routes (settings, dashboard) are handled inside RealApp.
function App() {
  return (
    <Routes>
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/s/:bookingId" element={<SharePage />} />
      {import.meta.env.DEV && (
        <Route path="/dev/scenarios" element={<ScenarioGallery />} />
      )}
      <Route path="*" element={<AppContent />} />
    </Routes>
  );
}

export default App;
